import { useRef, useState, type FormEvent } from "react";

export type ChatMessage = {
  id: number;
  role: "bot" | "user";
  text: string;
};

export const CHAT_GREETING =
  "Hi, ich bin dein Preisvergleich-Assistent. Frag mich nach Elektronik, Handys oder Computern — ich helfe dir, das beste Angebot zu finden.";

export const CHAT_FALLBACK_ANSWER =
  "Der Chat ist gerade nicht erreichbar. Schreib uns gern direkt über die Kontaktseite.";

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: 0, role: "bot", text: CHAT_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<Record<number, "up" | "down">>({});
  const nextId = useRef(1);

  const sendFeedback = (
    messageId: number,
    rating: "up" | "down",
    text: string,
  ) => {
    setFeedback((prev) => ({ ...prev, [messageId]: rating }));
    void fetch("/api/chat-feedback", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ messageId, rating, text }),
    }).catch(() => {});
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const history = [
      ...messages,
      { id: nextId.current++, role: "user" as const, text },
    ];
    setMessages(history);
    setInput("");
    setSending(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          messages: history.map((m) => ({
            role: m.role === "bot" ? "assistant" : "user",
            content: m.text,
          })),
        }),
      });
      if (!res.ok) throw new Error("chat request failed");
      const data: { ok: boolean; reply?: string } = await res.json();
      if (!data.ok || !data.reply) throw new Error("chat request failed");

      setMessages((prev) => [
        ...prev,
        { id: nextId.current++, role: "bot", text: data.reply! },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: nextId.current++, role: "bot", text: CHAT_FALLBACK_ANSWER },
      ]);
    } finally {
      setSending(false);
    }
  };

  return {
    messages,
    input,
    setInput,
    sending,
    feedback,
    sendFeedback,
    handleSubmit,
  };
}
